import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Globe, Lock, Users } from 'lucide-react';
import { usePostActions } from '../hooks/usePosts';
import { toast } from 'sonner';

interface EditPostModalProps {
  isOpen: boolean;
  onClose: () => void;
  postId: string;
  currentCaption?: string;
  currentPrivacy: 'public' | 'private' | 'followers_only';
}

const EditPostModal = ({ 
  isOpen, 
  onClose, 
  postId, 
  currentCaption = '', 
  currentPrivacy 
}: EditPostModalProps) => {
  const { t } = useTranslation();
  const [caption, setCaption] = useState(currentCaption);
  const [privacy, setPrivacy] = useState<'public' | 'private' | 'followers_only'>(currentPrivacy);
  const { updatePost, isUpdating } = usePostActions();

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setCaption(currentCaption);
      setPrivacy(currentPrivacy);
    }
  }, [isOpen, currentCaption, currentPrivacy]);

  const handleSave = () => {
    updatePost(postId, { caption, privacy_setting: privacy }, {
      onSuccess: () => {
        toast.success(t('modals.editPost.success'));
        onClose();
      },
      onError: (error) => {
        toast.error(t('modals.editPost.error', { error: error?.message || t('common.unknownError') }));
      }
    });
  };

  const privacyOptions = [
    { 
      value: 'public', 
      label: t('modals.editPost.privacyPublic'), 
      description: t('modals.editPost.privacyPublicDesc'),
      icon: Globe 
    },
    { 
      value: 'followers_only', 
      label: t('modals.editPost.privacyFollowers'), 
      description: t('modals.editPost.privacyFollowersDesc'),
      icon: Users 
    },
    { 
      value: 'private', 
      label: t('modals.editPost.privacyPrivate'), 
      description: t('modals.editPost.privacyPrivateDesc'),
      icon: Lock 
    },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('modals.editPost.title')}</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-6 py-4">
          {/* Caption */}
          <div className="space-y-2">
            <Label htmlFor="caption">{t('modals.editPost.caption')}</Label>
            <Textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              placeholder={t('modals.editPost.captionPlaceholder')}
              className="resize-none"
              rows={4}
            />
          </div>

          {/* Privacy Setting */}
          <div className="space-y-3">
            <Label>{t('modals.editPost.privacy')}</Label>
            <RadioGroup value={privacy} onValueChange={(value) => setPrivacy(value as typeof privacy)}>
              {privacyOptions.map((option) => (
                <div 
                  key={option.value} 
                  className="flex items-start space-x-3 p-3 rounded-lg border hover:bg-accent/50 transition-colors cursor-pointer"
                  onClick={() => setPrivacy(option.value as typeof privacy)}
                >
                  <RadioGroupItem value={option.value} id={option.value} className="mt-1" />
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <option.icon className="h-4 w-4 text-muted-foreground" />
                      <Label htmlFor={option.value} className="cursor-pointer font-medium">
                        {option.label}
                      </Label>
                    </div>
                    <p className="text-sm text-muted-foreground mt-1">
                      {option.description}
                    </p>
                  </div>
                </div>
              ))}
            </RadioGroup>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={isUpdating}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={isUpdating}>
            {isUpdating ? t('common.processing') : t('common.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default EditPostModal;
